import { defineAsyncComponent, h, type Plugin } from 'vue'
import mq from './js/mq'
import RogueButton from './components/core/RogueButton.vue'
import Carousel from './components/core/Carousel.vue'
import Column from './components/core/Column.vue'
import Icon from './components/core/Icon.vue'
import Row from './components/core/Row.vue'
import Block from './components/core/Block.vue'
import ClientOnly from './components/core/ClientOnly.vue'
import Spinner from './components/core/Spinner.vue'
import DynamicMedia from './components/core/DynamicMedia.vue'
import StaticImage from './components/core/StaticImage.vue'
import RenderBlock from './components/RenderBlock'
import Heading from './components/core/Heading.vue'
import ColumnLayout from './components/core/ColumnLayout.vue'
import Hero from './components/core/Hero.vue'
import CMS from './components/cms/CMS.vue'
import dragScroll from './js/drag-scroll'
import Field from './components/forms/Field.vue'
import RogueInput from './components/forms/RogueInput.vue'
import InfiniteCarouselLoading from './components/core/InfiniteCarouselLoading.vue'

import './styles/styles.scss'

const COMPONENTS = {
  // Core - loaded synchronously
  Block,
  RogueButton,
  Carousel,
  Column,
  Heading,
  Icon,
  RenderBlock,
  ColumnLayout,
  Row,
  CMS,
  RogueInput,
  Field,
  Spinner,
  DynamicMedia,
  StaticImage,
  Hero,
  ClientOnly,
  InfiniteCarouselLoading,

  // Core - loaded asynchronously (not needed on every page)
  BorderedBlock      : defineAsyncComponent(() => import('./components/cms/BorderedBlock.vue')),
  Stars              : defineAsyncComponent(() => import('./components/core/Stars.vue')),
  StickScroll        : defineAsyncComponent(() => import('./components/core/StickScroll.vue')),
  Tabs               : defineAsyncComponent(() => import('./components/cms/Tabs.vue')),
  HeroConfig         : defineAsyncComponent(() => import('./components/core/HeroConfig.vue')),
  ProductCarousel    : defineAsyncComponent(() => import('./components/cms/ProductCarousel.vue')),
  DistributorMap     : defineAsyncComponent(() => import('./components/cms/DistributorMap.vue')),
  DistributorSection : defineAsyncComponent(() => import('./components/cms/DistributorSection.vue')),
  DynamicPicture     : defineAsyncComponent(() => import(/* webpackChunkName: "beta" */ './components/core/DynamicPicture.vue')),
  PreloadImage       : defineAsyncComponent(() => import(/* webpackChunkName: "preload-image" */ './components/core/PreloadImage.vue')),

  // core/async
  Drawer        : defineAsyncComponent(() => import(/* webpackChunkName: "drawer" */ './components/core/Drawer.vue')),
  Box           : defineAsyncComponent(() => import(/* webpackChunkName: "drawer" */ './components/core/Box.vue')),
  Accordion     : defineAsyncComponent(() => import(/* webpackChunkName: "accordion" */'./components/core/async/Accordion.vue')),
  Message       : defineAsyncComponent(() => import(/* webpackChunkName: "message" */'./components/core/async/Message.vue')),
  Modal         : defineAsyncComponent(() => import(/* webpackChunkName: "modal" */'./components/core/async/Modal.vue')),
  Panel         : defineAsyncComponent(() => import(/* webpackChunkName: "panel" */'./components/core/async/Panel.vue')),
  Tooltip       : defineAsyncComponent(() => import(/* webpackChunkName: "tt" */'./components/core/async/Tooltip.vue')),
  YouTubeVideo  : defineAsyncComponent(() => import(/* webpackChunkName: "yt" */'./components/core/async/YouTubeVideo.vue')),
  YbvrVideo     : defineAsyncComponent(() => import(/* webpackChunkName: "ybvr" */'./components/core/async/YbvrVideo.vue')),
  Gallery       : defineAsyncComponent(() => import(/* webpackChunkName: "gallery" */'./components/core/async/Gallery.vue')),
  CarouselModal : defineAsyncComponent(() => import(/* webpackChunkName: "gallery" */'./components/core/async/CarouselModal.vue')),
  Media         : defineAsyncComponent(() => import(/* webpackChunkName: "gallery" */'./components/core/async/media/Media.vue')),

  // app
  EventsList      : defineAsyncComponent(() => import(/* webpackChunkName: "native-app" */ './components/app/EventsList.vue')),
  AppFeaturedItem : defineAsyncComponent(() => import(/* webpackChunkName: "native-app" */ './components/app/FeaturedItem.vue')),
  AppListItem     : defineAsyncComponent(() => import(/* webpackChunkName: "native-app" */ './components/app/ListItem.vue')),

  // cms
  LongFormContent      : defineAsyncComponent(() => import(/* webpackChunkName: "long-form-content" */'./components/cms/LongFormContent.vue')),
  LFCSection           : defineAsyncComponent(() => import(/* webpackChunkName: "long-form-content" */'./components/cms/LFCSection.vue')),
  CallToAction         : defineAsyncComponent(() => import(/* webpackChunkName: "cms" */'./components/cms/CallToAction.vue')),
  CMSForm              : defineAsyncComponent(() => import(/* webpackChunkName: "forms-cmsform" */'./components/cms/CMSForm.vue')),
  IconAction           : defineAsyncComponent(() => import(/* webpackChunkName: "cms" */'./components/cms/IconAction.vue')),
  Links                : defineAsyncComponent(() => import(/* webpackChunkName: "cms" */'./components/cms/Links.vue')),
  CMSTabs              : defineAsyncComponent(() => import(/* webpackChunkName: "cms" */'./components/cms/CMSTabs.vue')),
  InfiniteCarousel     : defineAsyncComponent(() => import(/* webpackChunkName: "cms" */'./components/core/InfiniteCarousel.vue')),
  ContentImageCarousel : defineAsyncComponent(() => import(/* webpackChunkName: "cms" */'./components/cms/ContentImageCarousel.vue')),
  RoutingTabs          : defineAsyncComponent(() => import(/* webpackChunkName: "cms" */'./components/cms/RoutingTabs.vue')),
  ZendeskForm          : defineAsyncComponent(() => import(/* webpackChunkName: "zendesk-form" */'./components/cms/ZendeskForm.vue')),
  Snipe                : defineAsyncComponent(() => import(/* webpackChunkName: "snipe" */'./components/cms/Snipe.vue')),
  ArchiveList          : defineAsyncComponent(() => import(/* webpackChunkName: "archive-list" */ './components/app/ArchiveList.vue')),
  ScheduleTable        : defineAsyncComponent(() => import(/* webpackChunkName: "schedule-table" */ './components/cms/ScheduleTable.vue')),
  ProductCategoryList  : defineAsyncComponent(() => import(/* webpackChunkName: "product-category-list" */ './components/cms/ProductCategoryList.vue')),
  RevolutionFundChart  : defineAsyncComponent(() => import(/* webpackChunkName: "revolution-fund" */ './components/cms/RevolutionFundChart.vue')),

  // Events
  EventScheduleV1      : defineAsyncComponent(() => import(/* webpackChunkName: "events" */ './components/events/EventScheduleV1.vue')),
  Athletes             : defineAsyncComponent(() => import(/* webpackChunkName: "events" */ './components/events/Athletes.vue')),
  Leaderboard          : defineAsyncComponent(() => import(/* webpackChunkName: "events" */ './components/cms/Leaderboard.vue')),
  Competition          : defineAsyncComponent(() => import(/* webpackChunkName: "events" */ './components/events/Competition.vue')),
  Shorts               : defineAsyncComponent(() => import(/* webpackChunkName: "events" */ './components/events/Shorts.vue')),
  VideoList            : defineAsyncComponent(() => import(/* webpackChunkName: "events" */ './components/events/VideoList.vue')),
  EventLeaderboard     : defineAsyncComponent(() => import(/* webpackChunkName: "events" */ './components/events/EventLeaderboard.vue')),
  EventAthleteCard     : defineAsyncComponent(() => import(/* webpackChunkName: "events" */ './components/events/EventAthleteCard.vue')),
  OverallLeaderboard   : defineAsyncComponent(() => import(/* webpackChunkName: "events" */ './components/events/OverallLeaderboard.vue')),
  OverallAthleteCard   : defineAsyncComponent(() => import(/* webpackChunkName: "events" */ './components/events/OverallAthleteCard.vue')),
  EventSchedule        : defineAsyncComponent(() => import(/* webpackChunkName: "events" */ './components/events/EventSchedule.vue')),
  HlsVideo             : defineAsyncComponent(() => import(/* webpackChunkName: "video" */ './components/events/HlsVideo.vue')),
  StrongestLeaderboard : defineAsyncComponent(() => import(/* webpackChunkName: "leaderboard" */ './components/cms/StrongestLeaderboard.vue')),

  // forms
  Date                  : defineAsyncComponent(() => import(/* webpackChunkName: "forms-date" */'./components/forms/Date.vue')),
  FileUpload            : defineAsyncComponent(() => import(/* webpackChunkName: "forms-fileupload" */'./components/forms/FileUpload.vue')),
  Radio                 : defineAsyncComponent(() => import(/* webpackChunkName: "forms" */'./components/forms/Radio.vue')),
  RadioButtonGroup      : defineAsyncComponent(() => import(/* webpackChunkName: "forms" */'./components/forms/RadioButtonGroup.vue')),
  Recaptcha             : defineAsyncComponent(() => import(/* webpackChunkName: "forms-recaptcha" */'./components/forms/Recaptcha.vue')),
  RogueSelect           : defineAsyncComponent(() => import(/* webpackChunkName: "forms" */'./components/forms/RogueSelect.vue')),
  Stepper               : defineAsyncComponent(() => import(/* webpackChunkName: "forms" */'./components/forms/Stepper.vue')),
  Swatches              : defineAsyncComponent(() => import(/* webpackChunkName: "forms" */'./components/forms/Swatches.vue')),
  Checkbox              : defineAsyncComponent(() => import(/* webpackChunkName: "forms" */'./components/forms/Checkbox.vue')),
  ToggleSwitch          : defineAsyncComponent(() => import(/* webpackChunkName: "forms" */'./components/forms/ToggleSwitch.vue')),
  RogueTextarea         : defineAsyncComponent(() => import(/* webpackChunkName: "forms-textarea" */'./components/forms/RogueTextarea.vue')),
  DragAndDropFileUpload : defineAsyncComponent(() => import(/* webpackChunkName: "forms-drag-and-drop-file-upload" */'./components/forms/DragAndDropFileUpload.vue')),
  TextSwatches          : defineAsyncComponent(() => import(/* webpackChunkName: "forms-textswatches" */'./components/forms/TextSwatches.vue')),

  // pdp - ProductPage has its own dynamic imports by layout, so we dont need to load all children
  // only the ones that will be rendered in RenderBlock
  ProductPage             : defineAsyncComponent(() => import(/* webpackChunkName: "pf-product" */'./components/pdp/ProductPage.vue')),
  ButtonBar               : defineAsyncComponent(() => import(/* webpackChunkName: "pdp" */'./components/core/async/ButtonBar.vue')),
  SwatchLinks             : defineAsyncComponent(() => import(/* webpackChunkName: "pdp" */'./components/pdp/SwatchLinks.vue')),
  MediaImages             : defineAsyncComponent(() => import(/* webpackChunkName: "media-images" */'./components/core/async/media/MediaImages.vue')),
  ProductTabs             : defineAsyncComponent(() => import(/* webpackChunkName: "pf-product" */'./components/pdp/components/ProductTabs.vue')),
  GearSpecs               : defineAsyncComponent(() => import(/* webpackChunkName: "pf-product" */'./components/pdp/components/GearSpecs.vue')),
  GearSpecsWide           : defineAsyncComponent(() => import(/* webpackChunkName: "pdp" */'./components/pdp/components/GearSpecsWide.vue')),
  ProductFeatureGrid      : defineAsyncComponent(() => import(/* webpackChunkName: "pf-product" */'./components/pdp/components/ProductFeatureGrid.vue')),
  ProductFeature          : defineAsyncComponent(() => import(/* webpackChunkName: "pf-product" */'./components/pdp/components/ProductFeature.vue')),
  IconFeature             : defineAsyncComponent(() => import(/* webpackChunkName: "pf-product" */'./components/pdp/components/IconFeature.vue')),
  Durometer               : defineAsyncComponent(() => import(/* webpackChunkName: "pdp-durometer" */'./components/pdp/components/Durometer.vue')),
  Hotspot                 : defineAsyncComponent(() => import(/* webpackChunkName: "pdp-hotspot" */'./components/pdp/components/Hotspot.vue')),
  ProductInstructionsList : defineAsyncComponent(() => import(/* webpackChunkName: "pf-product" */'./components/pdp/components/ProductInstructionsList.vue')),
  ImageFlag               : defineAsyncComponent(() => import(/* webpackChunkName: "pf-product" */ './components/core/ImageFlag.vue')),
}

const SisuPlugin: Plugin = {
  install(app) {
    app.use(mq)

    // RogueLink should be defined in SPA, but for compatability we need to define it. Just an a tag will work...
    if (!app.component('RogueLink')) {
      app.component('RogueLink', (props: { to: string }, {slots, attrs}) => {
        return h('a', { href: props.to, ...attrs }, slots.default ? slots.default() : [])
      })
    }

    app.directive('DragScroll', dragScroll)

    Object.keys(COMPONENTS)
      .forEach((componentName) => {
        app.component(componentName, COMPONENTS[componentName])
      })
  },
}

export default SisuPlugin
